class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    // Separate, longer debounce than the 300ms results-fetch one above: only
    // track once the visitor has actually paused typing, not on every
    // keystroke. Most searches never reach the /search results page (a
    // visitor picks a predictive result directly), so this -- plus the
    // click tracking below -- is the only way most completed searches get
    // captured at all.
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.trackSearchPause.bind(this), 1500));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('click', this.handleResultClick.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.lastTrackedQuery = null;

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.lastTrackedQuery = null;
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // 2026-09-23: own request -- "is the search not indexing articles?".
      // Confirmed via a direct /search/suggest.json call: Shopify's
      // predictive search omits "article" from its resources[type] default
      // entirely (the default returns collections/pages/queries/products,
      // never articles) -- so the Enrich Me blog was structurally
      // unreachable from search, not a content/indexing problem. Listing
      // every resource type explicitly here is what actually surfaces
      // articles in the dropdown.
      // 2026-10-03 (owner): "query" (Shopify's autocomplete Suggestions) dropped --
      // it duplicated the product/collection matches and pushed them down.
      // Re-add ',query' here to bring the Suggestions row back.
      url.searchParams.set('resources[type]', 'product,collection,page,article'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  trackSearchPause() {
    const query = this.getQuery();
    if (query.length === 0) return;
    this.trackSearchEvent(query, 'predictive_pause');
  }

  handleResultClick(event) {
    const link = event.target.closest('a');
    if (!link || !this.searchContent || !this.searchContent.contains(link)) return;
    const query = this.getQuery();
    if (query.length === 0) return;
    this.trackSearchEvent(query, 'predictive_click');
  }

  trackSearchEvent(query, source) {
    // A visitor pausing mid-edit re-triggers the pause debounce for the same
    // query more than once (e.g. moving focus away and back) -- only track
    // a given query once per pause, though a later click on the same query
    // still tracks separately since it's a distinct, more meaningful signal.
    if (source === 'predictive_pause') {
      if (this.lastTrackedQuery === query) return;
      this.lastTrackedQuery = query;
    }
    // Klaviyo's onsite-embed app block stubs window.klaviyo as a queue array
    // before its real SDK loads, so push() is safe here regardless of load
    // order. Klaviyo's client also delivers track() calls via sendBeacon,
    // so this survives the click case even when a navigation follows
    // immediately after.
    if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      window.klaviyo.push([
        'track',
        'Searched Site',
        {
          SearchTerm: query,
          Source: source,
        },
      ]);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
